<script setup>
import { computed, ref, watchEffect, nextTick, onMounted, watch } from 'vue';
import { useQuery } from 'villus';
import find from 'lodash-es/find';
import each from 'lodash-es/each';
import { useRoute, useRouter } from 'vue-router';
import { GA } from '@/mixins/Ga';
import CustomSelect from '@/components/custom-select/CustomSelect.vue';
import { useStore } from '@/store/Store.js';
import { useLenis } from '@/components/scroll/Lenis.js';
import WaveTop from '@/icons/wave-top.svg';
import WaveCurve from '@/icons/wave-curve.svg';
import WaveTopSmall from '@/icons/wave-top-small.svg';
import Filter from '@/icons/filter.svg';
import Loader from '@/components/loader/Loader.vue';
import Card from '@/components/card/Card.vue';
import BlogFeaturedList from '@/components/blog-cta/BlogFeaturedList.vue';
import StickyBar from '@/components/sticky-bar/StickyBar.vue';
import Header from '@/components/header/Header.vue';

import FlexibleQuery from '@/components/flexible/FlexibleQuery';
import { imageTransform } from '@/components/flexible/FlexibleQuery.js';

const store = useStore();
const route = useRoute();
const router = useRouter();
const menu = ref([]);
const limit = ref(12);
const offset = ref(0);
const search = ref('');
const currentCategory = ref(null);
const currentIngredient = ref(null);
const recipes = ref([]);
const hasMore = ref(true);
const preselectedIngredient = ref('');
let once = false;
const routeId = route.params.id;
let loadingPagination = false;
const listkey = ref('recipestart');
const themeColor = ref('red');
const crumbs = ref([]);
const lenis = useLenis();
const previousScroll = ref(0);
const mounted = ref(false);
const thedata = ref(null);
const rootEl = ref(null);

const defaultAllIngredients = {
    id: null,
    slug: 'all',
    title: 'Ingrédients',
};
const defaultRecipeType = {
    id: null,
    slug: 'all',
    title: 'Régime',
};
const defaultCategorieType = {
    id: null,
    slug: 'all',
    title: 'Catégorie',
};

if (route.query.ingredient) {
    preselectedIngredient.value = route.query.ingredient;
    currentIngredient.value = route.query.ingredient;
}

const setIngredientOrRecipeType = (e) => {
    if (e.id) {
        router.push({
            id: 'recettes',
            params: {
                category: e.slug,
                slug: e.slug,
                id: e.id,
            },
        });
    } else {
        router.push({
            name: `recettes.${store.lang.value}`,
        });
    }
};

const more = () => {
    loadingPagination = true;
    offset.value += limit.value;
    previousScroll.value = store.windowScroll.value.y;
};

/**
 * Main recipes query
 */
const variables = computed(() => {
    const params = {
        lang: store.lang.value,
        limit: limit.value,
        offset: offset.value,
        order: search.value === '' ? 'postDate DESC' : 'score DESC',
        search: search.value,
    };

    if (route.params.id) {
        params.id = ['and', route.params.id];
    }

    return params;
});

const recettesQuery = `
    query FetchEntries($lang: [String], $limit: Int, $offset: Int, $id: [QueryArgument], $search: String, $order: String) {
        intro: entry (section: "recettes", site: $lang) {
            title,
            ... on recettes_Entry {
                color
                subtitle
                suptitle
                supsubtitle
                personnageEntete {
                    inline
                }
                sectionHandle
                description: productsDescription,
                cover: productsCover ${imageTransform(2000)}
                coverMobile: productsCoverMobile ${imageTransform(800)}
                featuredRecipesTitle
                featuredRecipes {
                    id,
                    title,
                    uri,
                    ... on recette_Entry {
                        category: recetteCategory { ... on recettescategories_default_Entry { title } },
                        cover: recetteCover ${imageTransform(580)}
                    }
                }
            }
        },
        recipes : entry (section: "recettes", site: $lang) {
            ... on recettes_Entry {
                recettesRecetteCta
            }
        },
        entries (
                section: "recette",
                site: $lang,
                orderBy: $order,
                limit: $limit,
                offset: $offset,
                relatedTo: $id,
                search: $search
            ) {
            id,
            title,
            uri,
            ... on recette_Entry {
                category: recetteCategory { ... on recettescategories_default_Entry { title } },
                cover: recetteCover ${imageTransform(580)},
                largecover: recetteCover ${imageTransform(1280)}
            }
        },
        total: entryCount(section: "recette", site: $lang, orderBy: $order, limit: $limit, offset: $offset, relatedTo: $id, search: $search),
        categories: entries (site: $lang, section: "recettesCategories") {
            id,
            slug,
            title,
            uri
        },
        ingredients: entries(site: $lang, section: "ingredients", orderBy: "title ASC") {
            id,
            slug,
            title,
            uri,
            ... on ingredients_default_Entry {
                blogCategoryTitle,
                flexible : ${FlexibleQuery()}
            }
            
        },
        recipeTypes: entries (site: $lang, section: "recettesTypes") {
            id,
            slug,
            title,
            uri
        }
    }
`;
onMounted(() => {
    themeColor.value = thedata.value?.intro?.color ?? 'red';
    store.setHeaderColor({
        mainColor: themeColor.value,
        wave: `light-${themeColor.value}`,
        theme: 'light',
        normal: { bg: 'white', txt: `light-${themeColor.value}` },
        over: { bg: 'white', txt: `light-${themeColor.value}` },
        prenav: {
            bg: `bg-dark-${themeColor.value}`,
            txt: 'text-white',
        },
        nav: {
            txt: 'text-white',
            hover: `bg-dark-${themeColor.value}`,
            burger: 'text-white',
            lines: `text-light-${themeColor.value}`,
        },
    });

    mounted.value = true;
});

const {
    data: mainData,
    isFetching,
    onData,
} = await useQuery({
    variables,
    query: recettesQuery,
});

onData((data) => {
    thedata.value = data;
    let preserveScroll = false;
    if (previousScroll.value === store.windowScroll.value.y) {
        preserveScroll = true;
    }

    // reset the data completely only if we're not loading pagination
    if (!loadingPagination) {
        recipes.value = [];
    }

    recipes.value.push(...data.entries);
    hasMore.value = recipes.value.length != data.total;
    // console.log(route);
    // console.log([...data.categories, ...data.ingredients, ...data.recipeTypes]);
    currentCategory.value = find([...data.categories, ...data.ingredients, ...data.recipeTypes], (category) => category.slug == route.params.category);

    // console.log(currentCategory.value);

    listkey.value = recipes.value.map((r) => r.id).join('');

    if (currentCategory.value) {
        crumbs.value = [
            { url: '/recettes', text: 'Recettes' },
            { url: currentCategory.value.url, text: currentCategory.value.title },
        ];
    }

    // if (!currentCategory.value) {
    //     currentCategory.value = data.categories[0];
    // }

    menu.value = [];
    each(data.categories, (category, index) => {
        menu.value.push({
            title: category.title,
            to: index == 0 ? { params: { category: null, id: null } } : { params: { category: category.slug, id: category.id, noscroll: true } },
        });
    });

    // activate icon per option
    each(data.recipeTypes, (theRecipeType, index) => {
        theRecipeType.icon = true;
        theRecipeType.iconSuffix = '-small';
    });

    nextTick(() => {
        if (!once) {
            once = true;
        }

        if (preserveScroll) {
            lenis.value.scrollTo(previousScroll.value, { immediate: true });
        }
    });

    loadingPagination = false;
});
</script>

<template>
    <div
        ref="rootEl"
        class="Recipes"
        :class="[pageClasses]"
    >
        <template v-if="mounted && mainData && mainData.intro">
            <div
                :class="['text-light-' + themeColor]"
                class="Waves absolute left-0 top-0 z-0 w-full overflow-x-hidden"
            >
                <WaveTop
                    class="max-w-screen mt-[-80px] hidden h-auto w-[1920px] fill-current md:block lg:mt-0"
                    :class="['text-light-' + themeColor]"
                />
                <WaveTopSmall
                    class="block h-auto w-full fill-current md:hidden"
                    :class="['text-light-' + themeColor]"
                />
            </div>
            <div class="Recipes__content Page__content">
                <Header
                    :data="mainData.intro"
                    :color="'light-' + mainData.intro.color"
                    :title-color="'dark-' + mainData.intro.color"
                    :title="search !== '' ? ui.search_recipe_title || 'Rechercher' : mainData.intro.title"
                    :suptitle="currentCategory?.title || ''"
                    :crumbs="crumbs"
                    type-pancarte="sentier"
                />

                <StickyBar
                    :colors="{
                        txt: 'white',
                        bg: 'dark-' + themeColor,
                    }"
                >
                    <div class="flex w-[48%] items-center font-bold md:mr-4 md:w-auto">
                        <Filter class="h-4 w-auto fill-current" />
                        <div class="ml-2 md:ml-5">Filtre</div>
                    </div>

                    <div class="filtres hidden gap-2 md:ml-auto md:block md:gap-4">
                        <CustomSelect
                            :options="[defaultCategorieType, ...mainData.categories]"
                            :preselected-id="routeId"
                            class="select select-categorie-type relative z-10 mr-2.5 w-[48%] md:w-auto"
                            :colors="{ bg: 'light-' + themeColor, text: 'white' }"
                            :side="portrait ? 'right' : 'left'"
                            v-on:input="setIngredientOrRecipeType($event)"
                        />

                        <CustomSelect
                            :options="[defaultRecipeType, ...mainData.recipeTypes]"
                            :preselected-id="routeId"
                            class="select select-recipe-type relative z-9 mr-2.5 w-[48%] md:w-auto"
                            :colors="{ bg: 'light-' + themeColor, text: 'white' }"
                            v-on:input="setIngredientOrRecipeType($event)"
                        />

                        <CustomSelect
                            :options="[defaultAllIngredients, ...mainData.ingredients]"
                            :preselected-id="routeId"
                            class="select select-ingredients relative z-8 w-[48%] md:w-auto"
                            :colors="{ bg: 'light-' + themeColor, text: 'white' }"
                            :side="portrait ? 'right' : 'left'"
                            v-on:input="setIngredientOrRecipeType($event)"
                        />
                    </div>
                    <CustomSelect
                        :options="[defaultCategorieType, ...mainData.categories]"
                        :preselected-id="routeId"
                        class="select select-categorie-type relative z-10 mb-2.5 w-[48%] md:hidden md:w-auto"
                        :colors="{ bg: 'light-' + themeColor, text: 'white' }"
                        :side="portrait ? 'right' : 'left'"
                        v-on:input="setIngredientOrRecipeType($event)"
                    />

                    <CustomSelect
                        :options="[defaultRecipeType, ...mainData.recipeTypes]"
                        :preselected-id="routeId"
                        class="select select-recipe-type relative z-9 w-[48%] md:hidden md:w-auto"
                        :colors="{ bg: 'light-' + themeColor, text: 'white' }"
                        v-on:input="setIngredientOrRecipeType($event)"
                    />

                    <CustomSelect
                        :options="[defaultAllIngredients, ...mainData.ingredients]"
                        :preselected-id="routeId"
                        class="select select-ingredients relative z-8 w-[48%] md:hidden md:w-auto"
                        :colors="{ bg: 'light-' + themeColor, text: 'white' }"
                        :side="portrait ? 'right' : 'left'"
                        v-on:input="setIngredientOrRecipeType($event)"
                    />
                </StickyBar>

                <!-- <BlogFeaturedList
                    v-if="!currentCategory"
                    class="mb-12"
                    :title-top="mainData.intro.featuredRecipesTitle"
                    :color="'dark-'+themeColor"
                    :blogs="mainData.intro.featuredRecipes"
                /> -->

                <div
                    :key="listkey"
                    class="Blogs__blocks relative"
                >
                    <h3
                        v-if="!currentCategory"
                        class="h4 relative z-10 mb-4 px-6 pt-12 text-white md:mb-10 md:pt-0 xl:px-20"
                        :class="['md:text-dark-' + themeColor]"
                    >
                        {{ mainData.ingredients.find((ingredient) => ingredient.id === routeId)?.blogCategoryTitle || 'Toutes nos recettes' }}
                    </h3>

                    <div
                        class="z-10 relative"
                        v-if="mainData.ingredients.find((ingredient) => ingredient.id === routeId)?.flexible"
                    >
                        <div
                            class="px-6 xl:px-20 -mt-6 pb-5"
                            v-html="mainData.ingredients.find((ingredient) => ingredient.id === routeId)?.flexible[0]?.wysiwyg"
                        ></div>
                    </div>

                    <div
                        class="relative z-10 grid grid-cols-1 gap-8 px-6 md:grid-cols-2 lg:grid-cols-3 xl:gap-16 xl:px-20"
                        :class="['text-dark-' + themeColor]"
                    >
                        <template
                            v-for="recipe in recipes"
                            :key="'recipe' + recipe.uri"
                        >
                            <Card
                                :post="recipe"
                                class="text-white"
                            />
                        </template>
                        <div
                            v-if="recipes.length == 0"
                            :class="['text-dark-' + themeColor]"
                            class="my-12 w-full px-6 text-center text-xl md:col-span-2"
                        >
                            Aucune recette ne correspond à vos critères de recherche.
                        </div>
                    </div>

                    <div
                        aria-hidden
                        class="FeaturedGrid__background absolute bottom-0 left-0 z-0 h-full w-full md:h-[calc(100%-10rem)] xl:block"
                        :class="['bg-light-' + themeColor]"
                    >
                        <WaveCurve
                            :class="['text-light-' + themeColor]"
                            class="absolute top-[2px] h-auto w-full -translate-y-full fill-current md:hidden"
                        />
                        <WaveTop
                            :class="['text-light-' + themeColor]"
                            class="absolute top-0 hidden h-auto w-full translate-y-[-15%] rotate-180 -scale-x-100 fill-current md:block"
                        />
                    </div>
                </div>

                <div
                    class="flex justify-center py-8 xl:py-12"
                    :class="['bg-light-' + themeColor]"
                >
                    <button
                        v-if="hasMore"
                        class="BtnPagination group mx-auto inline-flex cursor-pointer items-center rounded-xl px-4 py-2 text-sm font-medium text-white"
                        :class="[
                            'bg-dark-' + themeColor,
                            {
                                'opacity-50': isFetching,
                            },
                        ]"
                        :disabled="isFetching"
                        v-on:click.stop="more"
                    >
                        <span>Voir plus de recettes</span>
                        <Loader
                            v-if="isFetching"
                            size="small"
                            class="ml-2"
                            bg="transparent"
                            line="white"
                        />
                    </button>
                </div>
            </div>
        </template>
    </div>
</template>
